import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import axiosInstance from 'src/api/axiosInstance';
import store from 'src/redux/store';
import { setStepData, selectIsCacheValid } from 'src/redux/slices/masterDataSlice';

const FORM_TYPE = 'cand';
const ENDPOINT = '/rest/candidate/fetchCandMastersByStep';
const STEPS_WITH_DATA = [0, 1, 2, 3, 4, 5, 6];
const PREFETCH_DELAY_MS = 1500; // 1.5 second delay after current step renders

/**
 * Custom Hook: Silently prefetches the NEXT step's master data in the background.
 *
 * Uses store.getState() at call time to check live cache validity,
 * avoiding stale-closure bugs on the isNextCacheValid selector.
 *
 * @param {number} currentStep - The active step index (0-based)
 */
export function useCandMasterDataPrefetch(currentStep) {
  const dispatch = useDispatch();
  const nextStep = currentStep + 1;
  const nextStepHasData = STEPS_WITH_DATA.includes(nextStep);

  useEffect(() => {
    if (!nextStepHasData) return undefined;

    // Delay prefetch so it doesn't block current step UI rendering
    const timer = setTimeout(async () => {
      // Read cache validity from Redux state at call time (not from a stale closure)
      const currentState = store.getState();
      const isCacheValidNow = selectIsCacheValid(FORM_TYPE, nextStep)(currentState);

      if (isCacheValidNow) return; // Already cached — skip

      const token = sessionStorage.getItem('token');
      try {
        const res = await axiosInstance.get(`${ENDPOINT}?step=${nextStep}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        dispatch(setStepData({ formType: FORM_TYPE, step: nextStep, data: res.data }));
      } catch (err) {
        // Non-critical: prefetch failures are silently ignored
        console.warn(`[Prefetch] Step ${nextStep} prefetch failed silently:`, err);
      }
    }, PREFETCH_DELAY_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);
}